import { session as sessionStorage } from '@packages/domain/utils/storage/session';
import { Box } from 'components/Box/Box';
import { RedirectToExternalUrl } from 'components/RedirectToExternalUrl/RedirectToExternalUrl';
import { SpinnerOverlay } from 'components/SpinnerOverlay/SpinnerOverlay';
import { Button } from 'components/ui/Button/Button';
import { Heading } from 'components/ui/Heading/Heading';
import { NOOP } from 'constants/actions';
import { InstructionsType } from 'constants/index';
import { useModalContext } from 'hooks';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslations } from 'utils/translations/useTranslations';
import { BillPayInstructions } from './InstructionsType/BillPayInstructions';
import { BPAYInstructions } from './InstructionsType/BPAYInstructions';
import { EmailInstructions } from './InstructionsType/EmailInstructions';
import { QRInstructions } from './InstructionsType/QRInstructions';
import { RedirectionInstructions } from './InstructionsType/RedirectionInstructions';
import { ReactComponent as Icon } from './ico-complete-payment.svg';
import { OnlineScript } from './OnlineScript';

import './Online.scss';

const FIVE_SECONDS_IN_MS = 5000;
const InstructionsComponents = {
  [InstructionsType.QR]: QRInstructions,
  [InstructionsType.EMAIL]: EmailInstructions,
  [InstructionsType.REDIRECT]: RedirectionInstructions,
  [InstructionsType.BPAY]: BPAYInstructions,
  [InstructionsType.BILLPAY]: BillPayInstructions,
};

function OnlineComponent({
  embeddedScript,
  forceRedirectInNewWindow = false,
  instructionsType,
  isBoleto = false,
  isOppOfferSelected = false,
  locale,
  offerName,
  paymentLinks,
  price,
  publicKey,
  redirectUrl: { value: partnerUrl } = {},
  returnUrl,
  scriptSignature,
  uuidTokenInstruction,
  paymentId,
  paymentOption,
  instructions,
  recipientName,
  onClose,
  onCancel,
}) {
  const i18n = useTranslations();
  const [redirectToUrl, setRedirectUrl] = useState(false);
  const shouldRedirect = redirectToUrl && partnerUrl;
  const { dispatch } = useModalContext();

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want it to run on mount
  useEffect(() => {
    sessionStorage.setItem('paymentLinks', JSON.stringify(paymentLinks));
  }, []);

  useEffect(() => {
    dispatch({ type: 'ONLINE_PAYMENT_INITIATED' });
  }, [dispatch]);

  function handleRedirectInNewWindow(evt) {
    evt.preventDefault();
    window.open(evt.currentTarget.href, '_blank', 'noreferrer');

    if (returnUrl) {
      window.parent.location.href = returnUrl;
      return;
    }

    window.parent.close();
  }

  const instructionsTypeComponent = InstructionsComponents[instructionsType];

  if (instructionsTypeComponent) {
    return React.createElement(instructionsTypeComponent, {
      embeddedScript,
      locale,
      i18n,
      publicKey,
      scriptSignature,
      returnUrl,
      partnerUrl,
      paymentId,
      paymentOption,
      offerName,
      forceRedirectInNewWindow,
      price,
      instructions,
      recipientName,
      onClose,
      onCancel,
    });
  }

  if (embeddedScript) {
    return (
      <OnlineScript
        embeddedScript={embeddedScript}
        i18n={i18n}
        locale={locale}
        offerName={offerName}
        isOppOfferSelected={isOppOfferSelected}
        price={price}
        publicKey={publicKey}
        scriptSignature={scriptSignature}
        uuidTokenInstruction={uuidTokenInstruction}
      />
    );
  }

  return (
    <Box>
      {shouldRedirect && (
        <SpinnerOverlay
          message={i18n.t('online.redirectMessage', { name: offerName })}
        >
          <RedirectToExternalUrl url={partnerUrl} delay={FIVE_SECONDS_IN_MS} />
        </SpinnerOverlay>
      )}
      <Heading as="h2" size="large" className="fontWeight-semiBold">
        {i18n.t('online.script.title')}
      </Heading>
      <div className="textAlign-center">
        <Icon />
        <p>{i18n.t('online.script.description')}</p>
        {forceRedirectInNewWindow ? (
          <a
            className="Button Button--primary Online-button"
            href={partnerUrl}
            target="_blank"
            onClick={isBoleto ? handleRedirectInNewWindow : NOOP}
            rel="noreferrer"
          >
            {i18n.t('online.instructions.button')}
          </a>
        ) : (
          <Button
            id="payment-trigger"
            type="button"
            onClick={() => setRedirectUrl(true)}
            tone="primary"
            size="full"
          >
            {i18n.t('online.instructions.button')}
          </Button>
        )}
      </div>
    </Box>
  );
}

OnlineComponent.propTypes = {
  embeddedScript: PropTypes.object,
  forceRedirectInNewWindow: PropTypes.bool,
  instructionsType: PropTypes.string,
  isBoleto: PropTypes.bool,
  isOppOfferSelected: PropTypes.bool,
  locale: PropTypes.string,
  offerName: PropTypes.string,
  paymentId: PropTypes.string,
  paymentLinks: PropTypes.object,
  price: PropTypes.object.isRequired,
  publicKey: PropTypes.string,
  redirectUrl: PropTypes.shape({
    value: PropTypes.string,
  }),
  returnUrl: PropTypes.string,
  scriptSignature: PropTypes.object,
  uuidTokenInstruction: PropTypes.object,
};

export { OnlineComponent };
